/*****************************************************************
 *  CUSTOMER BUTLER - OPEN AND CLOSED ACTIVITIES (Zoho's columns)  *
 *  File: apps/web/src/pages/customer/recordActivities.js          *
 *  What it does: sorts what Butler knows about a customer (or a   *
 *  property) into Zoho's two related lists, each with Zoho's      *
 *  three columns - Tasks / Meetings / Calls - and picks the       *
 *  "Next action" for the Overview.                                *
 *    sortActivities(parts)   the one rule (pure, tested)          *
 *    buildHistory(parts)     Zoho's Timeline "History": every     *
 *                            row newest first, grouped by day     *
 *    useRecordActivities()   loads the three doors once           *
 *  Reads: the tasks door, the logged-activity door and the        *
 *  "from the other Butlers" door.  Writes: nothing.               *
 *****************************************************************/

/* WHY (2026-10-15, James via Fable, Zoho screenshots): Zoho's record page
   shows "Open Activities" and "Closed Activities" as three columns (Open
   Tasks / Open Meetings / Open Calls) and a "Next Action" panel beside the
   contact. The Butler words for each are below, written once. */

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - WHICH ROW GOES IN WHICH COLUMN                     *
 *  SECTION 2 - THE RULE                                           *
 *  SECTION 3 - LOADING THE DOORS ONCE                             *
 *  SECTION 4 - THE TIMELINE HISTORY (grouped by day)              *
 *****************************************************************/

import { useCallback, useEffect, useState } from 'react';
import {
  fetchCustomerTasks, fetchTimeline, fetchSuiteTimeline,
  fetchPropertyHistory, fetchPropertySuiteTimeline,
} from './customerApi';
// WHY (2026-10-15, BACKLOG 317): each event from another Butler gets its
// icon (and a chip, for a send pulled back) from the one look list.
import { lookForEvent } from './suiteEventLooks';

/*****************************************************************
 *  SECTION 1 - WHICH ROW GOES IN WHICH COLUMN                     *
 *****************************************************************/

// A visit booked (or moved) in Dispatch is an OPEN meeting until Dispatch
// says the job was done or the visit was cancelled.
const VISIT_BOOKED_EVENTS = ['dispatch.visit.scheduled', 'dispatch.visit.moved'];
const VISIT_ENDED_EVENTS = ['dispatch.job.completed', 'dispatch.visit.cancelled'];

// Logged activity words: a call goes in the Calls column, a visit or
// meeting in the Meetings column; email, text and notes stay on the
// Timeline (Zoho keeps those in its own Emails list, not in Activities).
const CALL_CHANNEL = 'call';
const MEETING_CHANNEL = 'meeting';

/* ---- whenOf(row) -- a row's moment as a number (0 when none) ---- */
function whenOf(row) {
  const rawWhen = row.occurredAt || row.completedAt || row.dueAt || row.createdAt || null;
  const asNumber = rawWhen ? new Date(rawWhen).getTime() : 0;

  return Number.isFinite(asNumber) ? asNumber : 0;
}

/* ---- newestFirst(rows) -- a sorted copy ---- */
function newestFirst(rows) {
  return [...rows].sort((firstRow, secondRow) => whenOf(secondRow) - whenOf(firstRow));
}

/* ---- soonestDueFirst(tasks) -- due tasks first, undated last ---- */
function soonestDueFirst(tasks) {
  return [...tasks].sort((firstTask, secondTask) => {
    const firstDue = firstTask.dueAt ? new Date(firstTask.dueAt).getTime() : Infinity;
    const secondDue = secondTask.dueAt ? new Date(secondTask.dueAt).getTime() : Infinity;

    return firstDue - secondDue;
  });
}

/*****************************************************************
 *  SECTION 2 - THE RULE                                           *
 *****************************************************************/

/* ---- sortActivities({ tasks, logged, suiteEntries }) -------------
   In: the customer's tasks, logged activities and the entries the
   other Butlers sent (any may be empty).
   Out: { open: { tasks, meetings, calls },
          closed: { tasks, meetings, calls },
          otherLoggedCount, nextAction }
   nextAction: { kind: 'task'|'meeting', words, when } or null.
   Never changes the rows it is handed.                          */
export function sortActivities({ tasks, logged, suiteEntries }) {
  const allTasks = Array.isArray(tasks) ? tasks : [];
  const allLogged = Array.isArray(logged) ? logged : [];
  const allEntries = Array.isArray(suiteEntries) ? suiteEntries : [];

  // ---- tasks: open or done ----
  const openTasks = soonestDueFirst(allTasks.filter((oneTask) => oneTask.status === 'open'));
  const doneTasks = newestFirst(allTasks.filter((oneTask) => oneTask.status !== 'open'));

  // ---- meetings: a booked visit is open until a later end event ----
  const lastEndedAt = allEntries
    .filter((oneEntry) => VISIT_ENDED_EVENTS.includes(oneEntry.eventName))
    .reduce((latestNow, oneEntry) => Math.max(latestNow, whenOf(oneEntry)), 0);
  const isStillBooked = (oneEntry) => VISIT_BOOKED_EVENTS.includes(oneEntry.eventName) && whenOf(oneEntry) > lastEndedAt;
  const openMeetings = newestFirst(allEntries.filter(isStillBooked));
  const loggedMeetings = allLogged.filter((oneItem) => oneItem.channel === MEETING_CHANNEL);
  const closedMeetings = newestFirst([...allEntries.filter((oneEntry) => !isStillBooked(oneEntry)), ...loggedMeetings]);

  // ---- calls: logged calls have happened; Butler books no future calls ----
  const closedCalls = newestFirst(allLogged.filter((oneItem) => oneItem.channel === CALL_CHANNEL));
  const otherLoggedCount = allLogged.length - closedCalls.length - loggedMeetings.length;

  // ---- the next action: the soonest open task, else the newest booked visit ----
  let nextAction = null;

  if (openTasks.length > 0) {
    nextAction = { kind: 'task', words: openTasks[0].title, when: openTasks[0].dueAt || null };
  } else if (openMeetings.length > 0) {
    nextAction = { kind: 'meeting', words: openMeetings[0].summary, when: openMeetings[0].occurredAt || null };
  }

  return {
    open: { tasks: openTasks, meetings: openMeetings, calls: [] },
    closed: { tasks: doneTasks, meetings: closedMeetings, calls: closedCalls },
    otherLoggedCount,
    nextAction,
  };
}

/*****************************************************************
 *  SECTION 3 - LOADING THE DOORS ONCE                             *
 *****************************************************************/

/* ---- readOrEmpty(doorCall) -- a door's list, or [] when it says no ---- */
async function readOrEmpty(doorCall) {
  try {
    const answer = await doorCall();

    return Array.isArray(answer) ? answer : [];
  } catch (doorProblem) {
    return [];
  }
}

/* ---- useRecordActivities({ customerId, propertyId }) -------------
   Out: { sorted (null while loading; sorted.history = the day
   groups for the Timeline), reload }.
   A property has no tasks door yet, so its Tasks columns are empty
   and its logged history comes from the property history door.  */
export function useRecordActivities({ customerId, propertyId }) {
  const [sorted, setSorted] = useState(null);

  const reload = useCallback(async () => {
    const isProperty = Boolean(propertyId);
    const tasks = isProperty ? [] : await readOrEmpty(() => fetchCustomerTasks(customerId));
    const logged = isProperty
      ? await readOrEmpty(() => fetchPropertyHistory(propertyId))
      : await readOrEmpty(() => fetchTimeline(customerId));
    const suiteEntries = isProperty
      ? await readOrEmpty(() => fetchPropertySuiteTimeline(propertyId))
      : await readOrEmpty(() => fetchSuiteTimeline(customerId));

    setSorted({ ...sortActivities({ tasks, logged, suiteEntries }), history: buildHistory({ tasks, logged, suiteEntries }) });
  }, [customerId, propertyId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { sorted, reload };
}

/*****************************************************************
 *  SECTION 4 - THE TIMELINE HISTORY                               *
 *  WHY (2026-10-15, Zoho tour frame 027): Zoho's Timeline is a    *
 *  "History" list grouped by day - the time, an icon, what        *
 *  happened, by whom. Butler's rows are its tasks, its logged     *
 *  activities and what the other Butlers sent.                    *
 *****************************************************************/

// The words and icon for each logged channel.
const LOGGED_LOOKS = {
  call: { words: 'Call logged', icon: 'phone' },
  email: { words: 'Email logged', icon: 'link' },
  sms: { words: 'Text message logged', icon: 'phone' },
  meeting: { words: 'Visit or meeting logged', icon: 'calendar' },
  note: { words: 'Note logged', icon: 'file' },
};

/* ---- dayKeyOf(whenNumber) -- "2026-10-14" on this device ---- */
function dayKeyOf(whenNumber) {
  const moment = new Date(whenNumber);
  const monthText = String(moment.getMonth() + 1).padStart(2, '0');
  const dayText = String(moment.getDate()).padStart(2, '0');

  return `${moment.getFullYear()}-${monthText}-${dayText}`;
}

/* ---- historyRowsFrom({ tasks, logged, suiteEntries }) -- one shape for all ---- */
function historyRowsFrom({ tasks, logged, suiteEntries }) {
  const taskRows = (tasks || []).map((oneTask) => ({
    id: `task-${oneTask.id}`,
    when: whenOf({ occurredAt: oneTask.status === 'open' ? oneTask.createdAt : (oneTask.completedAt || oneTask.updatedAt) }),
    icon: 'check',
    words: `${oneTask.status === 'open' ? 'Task added' : 'Task done'} - ${oneTask.title}`,
    byWhom: '',
  }));

  const loggedRows = (logged || []).map((oneItem) => {
    const look = LOGGED_LOOKS[oneItem.channel] || { words: 'Activity logged', icon: 'clock' };

    return {
      id: `logged-${oneItem.id}`,
      when: whenOf(oneItem),
      icon: look.icon,
      words: oneItem.subject ? `${look.words} - ${oneItem.subject}` : look.words,
      byWhom: '',
    };
  });

  const suiteRows = (suiteEntries || []).map((oneEntry) => {
    const eventLook = lookForEvent(oneEntry.eventName); // its icon and chip

    return {
      id: `suite-${oneEntry.id}`,
      when: whenOf(oneEntry),
      icon: eventLook.icon,
      chip: eventLook.chip,
      words: oneEntry.summary,
      linkUrl: oneEntry.linkUrl || '',
      byWhom: oneEntry.whoName || '',
    };
  });

  return [...taskRows, ...loggedRows, ...suiteRows].filter((oneRow) => oneRow.when > 0);
}

/* ---- buildHistory(parts) -------------------------------------------
   In: { tasks, logged, suiteEntries }.
   Out: [{ dayKey, rows: [{ id, when, icon, chip, words, byWhom, linkUrl }] }]
   (chip is only on rows from the other Butlers, and may be null)
   newest day first, newest row first inside a day.              */
export function buildHistory(parts) {
  const rows = historyRowsFrom(parts).sort((firstRow, secondRow) => secondRow.when - firstRow.when);
  const days = [];

  for (const oneRow of rows) {
    const dayKey = dayKeyOf(oneRow.when);
    const lastDay = days[days.length - 1];

    if (lastDay && lastDay.dayKey === dayKey) {
      lastDay.rows.push(oneRow);
    } else {
      days.push({ dayKey, rows: [oneRow] });
    }
  }

  return days;
}
